import {
  AppleFilled,
  LeftOutlined,
  MailOutlined,
  RightOutlined
} from '@ant-design/icons'
import { Box, Button, Col, Divider, Row, Text } from '@qonsoll/react-design'
import {
  HiddenScrollbarEmailModalStyles,
  ImgStyled,
  styles
} from './AuthOptions.styles'
import {
  useAppleLogin,
  useFacebookLogin,
  useVippsLogin
} from 'domains/Session/hooks'
import { useEffect, useMemo, useState } from 'react'

import ForgotPassword from 'pages/Auth/ForgotPassword'
import LoginWithEmail from 'pages/Auth/LoginWithEmail'
import { Modal } from 'antd'
import PATHS from 'pages/paths'
import PropTypes from 'prop-types'
import SignUpWithEmail from 'pages/Auth/SignUpWithEmail'
import facebookIcon from 'assets/Facebook.png'
import noImage from 'assets/NoImage.png'
import { useGetRewards } from 'domains/Reward/hooks/get'
import { useHistory } from 'react-router-dom'
import { useTranslations } from 'contexts/Translation'
import vippsIcon from 'assets/Vipps.png'

/** Warm browser cache for carousel URLs so navigation usually hits disk/memory cache. */
function prefetchRewardImages(urls) {
  urls.forEach((src) => {
    const img = new Image()
    img.src = src
  })
}

const AuthOptions = (props) => {
  const { isLogin } = props
  const history = useHistory()
  const { t } = useTranslations()

  const [rewards, rewardsLoading] = useGetRewards()
  const [rewardIndex, setRewardIndex] = useState(0)
  const [displayedRewardImageUrl, setDisplayedRewardImageUrl] = useState(null)

  const { facebookLogin, loading: facebookLoading } = useFacebookLogin()
  const { appleLogin, loading: appleLoading } = useAppleLogin()
  const { vippsLogin, loading: vippsLoading } = useVippsLogin()
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false)
  /** @type {'login'|'forgot'|'signup'} login flow defaults to login, signup flow defaults to signup */
  const defaultEmailModalStep = isLogin ? 'login' : 'signup'
  const [emailModalStep, setEmailModalStep] = useState(defaultEmailModalStep)

  const rewardImages = useMemo(() => {
    const urls =
      rewards
        ?.map((r) => r?.imageUrl)
        ?.filter((url) => typeof url === 'string' && url.trim().length) || []
    return urls
  }, [rewards])
  const canSlide = rewardImages.length > 1

  const targetRewardImageUrl = useMemo(() => {
    if (rewardsLoading) {
      return null
    }
    if (rewardImages.length > 0) {
      const len = rewardImages.length
      return rewardImages[((rewardIndex % len) + len) % len]
    }
    return noImage
  }, [rewardsLoading, rewardImages, rewardIndex])

  useEffect(() => {
    if (rewardImages.length > 0) {
      prefetchRewardImages(rewardImages)
    }
  }, [rewardImages])

  useEffect(() => {
    if (targetRewardImageUrl == null) {
      return
    }

    let cancelled = false
    const img = new Image()

    const commit = () => {
      if (!cancelled) {
        setDisplayedRewardImageUrl(targetRewardImageUrl)
      }
    }

    img.onload = commit
    img.onerror = commit
    img.src = targetRewardImageUrl

    if (img.complete && img.naturalWidth > 0) {
      commit()
    }

    return () => {
      cancelled = true
    }
  }, [targetRewardImageUrl])

  const goToTipWizard = () =>
    history.push(PATHS.UNAUTHENTICATED.NEWS_TIP_WIZARD)

  const goNextReward = () => {
    if (!canSlide) return
    setRewardIndex((i) => i + 1)
  }

  const openEmailModal = () => {
    setEmailModalStep(defaultEmailModalStep)
    setIsEmailModalVisible(true)
  }

  const closeEmailModal = () => {
    setIsEmailModalVisible(false)
    setEmailModalStep(defaultEmailModalStep)
  }

  const loginWithEmailGuestModeActions = {
    showForgotPasswordForm: () => setEmailModalStep('forgot'),
    handleLoginFormBack: closeEmailModal,
    showSignUpForm: () => setEmailModalStep('signup')
  }

  const signUpGuestModeActions = {
    handleSignUpFormBack: isLogin
      ? () => setEmailModalStep('login')
      : closeEmailModal
  }

  const goPrevReward = () => {
    if (!canSlide) return
    setRewardIndex((i) => i - 1)
  }

  return (
    <Row noGutters h="center">
      <Col cw={12}>
        <HiddenScrollbarEmailModalStyles />
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={styles.carouselRowGap}>
          <Button
            type="text"
            aria-label="prev"
            disabled={!canSlide}
            onClick={goPrevReward}
            icon={<LeftOutlined style={styles.carouselIcon} />}
          />

          <Box style={styles.rewardImageFrame}>
            {displayedRewardImageUrl ? (
              <ImgStyled
                decoding="async"
                loading="eager"
                src={displayedRewardImageUrl}
              />
            ) : null}

            <Box aria-hidden="true" style={styles.gridOverlayOuter}>
              <Box style={styles.gridLineVerticalLeft} />
              <Box style={styles.gridLineVerticalRight} />

              <Box style={styles.gridLineHorizontalUpper} />
              <Box style={styles.gridLineHorizontalLower} />
            </Box>
          </Box>

          <Button
            type="text"
            aria-label="next"
            disabled={!canSlide}
            onClick={goNextReward}
            icon={<RightOutlined style={styles.carouselIcon} />}
          />
        </Box>

        <Box mt={3} textAlign="center">
          <Text style={styles.headline} type="primary">
            {isLogin
              ? t('Log in') + ' / ' + t('Sign up')
              : t('Register and get points for your next tip') + '!'}
          </Text>
        </Box>

        {!isLogin && (
          <Box mt={1} mb={3} textAlign="center">
            <Text type="secondary" italic>
              {t(
                'You remain anonymous to the newspaper`s readers even if you log in'
              )}
              .
            </Text>
          </Box>
        )}
        <Box mt={1} mb={3} textAlign="center">
          <Text type="secondary">{t('Use one of the options below')}</Text>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={styles.authButtonsRowGap}>
          <Button
            aria-label="login-facebook"
            loading={facebookLoading}
            onClick={facebookLogin}
            style={styles.buttonFacebook}>
            {!facebookLoading ? (
              <img
                alt="Facebook"
                src={facebookIcon}
                style={styles.socialIcon}
              />
            ) : null}
          </Button>

          <Button
            aria-label="login-email"
            onClick={openEmailModal}
            style={styles.buttonEmail}
            icon={<MailOutlined style={styles.iconMail} />}
          />

          <Button
            aria-label="login-apple"
            loading={appleLoading}
            onClick={appleLogin}
            style={styles.buttonApple}
            icon={<AppleFilled style={styles.iconApple} />}
          />

          <Button
            aria-label="login-vipps"
            loading={vippsLoading}
            onClick={vippsLogin}
            style={styles.buttonVipps}>
            {!vippsLoading ? (
              <Box style={styles.vippsIconClip}>
                <img alt="Vipps" src={vippsIcon} style={styles.vippsIconImg} />
              </Box>
            ) : null}
          </Button>
        </Box>

        {isLogin && (
          <Box mt={3} mb={3} textAlign="center" px={3}>
            <Text type="secondary" italic>
              {t(
                'You remain anonymous to the newspaper`s readers even if you log in'
              )}
              .
            </Text>
          </Box>
        )}

        {isLogin && <Divider style={styles.dividerTop} />}

        {isLogin && (
          <Box width="100%" px={3} pb={0}>
            <Button
              block
              size="large"
              onClick={goToTipWizard}
              style={styles.buttonTipWizard}>
              {t('Tips without logging in')}
            </Button>
          </Box>
        )}

        <Modal
          centered
          footer={null}
          title={null}
          closable={true}
          visible={isEmailModalVisible}
          onCancel={closeEmailModal}
          wrapClassName="auth-options-email-modal"
          bodyStyle={styles.emailModalBody}>
          {emailModalStep === 'login' ? (
            <LoginWithEmail
              isGuestMode
              guestModeActions={loginWithEmailGuestModeActions}
            />
          ) : null}

          {emailModalStep === 'forgot' ? (
            <ForgotPassword
              isGuestMode
              guestModeActions={{
                handleForgotPasswordFormBack: () => setEmailModalStep('login')
              }}
            />
          ) : null}

          {emailModalStep === 'signup' ? (
            <SignUpWithEmail
              isGuestMode
              guestModeActions={signUpGuestModeActions}
            />
          ) : null}
        </Modal>
      </Col>
    </Row>
  )
}

AuthOptions.propTypes = {
  isLogin: PropTypes.bool
}

export default AuthOptions
